import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { EmailFlowsResponse, FlowEmail, FlowSummary } from '@/lib/api/email-flows/types';
import { areUpdatesDisabled, getTriggerEventName } from '../logic';
import { FLOW_TRIGGER, QUERY_KEYS } from '@/lib';

interface UseEmailMetadataProps {
    flowData: FlowSummary | undefined;
    emailData: FlowEmail | undefined;
    emailId?: number;
}

interface UseEmailMetadataReturn {
    emailCount: number;
    isFirstEmail: boolean;
    trigger: string;
    isFirstTimeBasedEmail: boolean;
    isCartAbandonedFlow: boolean;
    actionsDisabled: boolean;
    hasSentEmails: boolean;
    hasNewReferralTag: boolean;
    triggerEventName: string;
    canAddAnotherEmail: boolean;
}

export function useEmailMetadata({ flowData, emailData, emailId }: UseEmailMetadataProps): UseEmailMetadataReturn {
    const queryClient = useQueryClient();

    return useMemo(() => {
        const emailCount = flowData?.flow_emails?.length || 0;
        const isFirstEmail = emailId ? emailCount === 1 : emailCount === 0;
        const trigger = flowData?.trigger || flowData?.trigger_type || 'checkout';
        const isFirstTimeBasedEmail = trigger === 'time' && isFirstEmail;
        const isCartAbandonedFlow = trigger === 'cart-abandonment';

        // Check if actions should be disabled
        // 1. If email has been sent (individual email check)
        // 2. If broadcast has started (TIME-triggered flows only)
        const emailSent = emailData ? areUpdatesDisabled(emailData) : false;
        const broadcastStarted = flowData?.trigger === FLOW_TRIGGER.TIME && flowData?.data?.started === true;
        const actionsDisabled = emailSent || broadcastStarted;

        // Check if email has been sent
        // Get analytics from cached flows list data (includes analytics)
        const cachedFlowsData = queryClient.getQueryData<EmailFlowsResponse>([QUERY_KEYS.EMAIL_FLOWS_LIST]);
        const cachedFlow = cachedFlowsData?.eventTriggered?.find(f => f.flow_id === flowData?.flow_id);
        const flowEmailWithAnalytics: FlowEmail | undefined =
            emailId && cachedFlow?.flow_emails
                ? cachedFlow.flow_emails.find(e => e.flow_email_id === emailId)
                : undefined;

        // Use analytics from cached data if available, otherwise fall back to current flow data
        const emailAnalytics = flowEmailWithAnalytics?.analytics || emailData?.analytics;

        // Primary check: email analytics from cached flow data or individual email data
        // Fallback for time-based flows: check flow-level recipient_count if email analytics not available
        const hasSentEmails =
            emailAnalytics?.sent && emailAnalytics.sent > 0
                ? true
                : trigger === FLOW_TRIGGER.TIME &&
                  flowData?.data?.started === true &&
                  (flowData?.recipient_count ?? 0) > 0;

        // Check if flow has new_referral tag
        const tags = flowData?.data?.specific_time?.tags || [];
        const hasNewReferralTag = tags.some((tag: { name: string }) => tag.name === 'new_referral');

        const triggerEventName = getTriggerEventName(trigger, hasNewReferralTag);

        // Check if can add another email (cart abandoned flows max 2 emails)
        const canAddAnotherEmail = !(isCartAbandonedFlow && emailCount >= 2);

        return {
            emailCount,
            isFirstEmail,
            trigger,
            isFirstTimeBasedEmail,
            isCartAbandonedFlow,
            actionsDisabled,
            hasSentEmails,
            hasNewReferralTag,
            triggerEventName,
            canAddAnotherEmail,
        };
    }, [flowData, emailData, emailId, queryClient]);
}
